import React, { useRef, useEffect, useState } from 'react'

import { twMerge } from 'tailwind-merge'
import { twJoin } from 'tailwind-merge'
import { tv } from 'tailwind-variants'

import { Icon } from '@components/Icon'
import {
  Field,
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react'
import { trans, SELECT, SELECTED_PLURAL } from '@helpers/translator'

export type SelectValueProps = {
  value: string | number
  label: string
  selected?: boolean
} | null

interface SelectProps {
  label?: string
  options: SelectValueProps[]
  value?: SelectValueProps | SelectValueProps[]
  onChange?: (value: SelectValueProps | SelectValueProps[]) => void
  buttonClassName?: string
  placeholder?: string
  multiple?: boolean
  theme?: 'light' | 'transparent'
  className?: string
  openUpwards?: boolean
  maxHeight?: number
}

const selectStyles = tv({
  slots: {
    base: 'flex items-center gap-x-3',
    button:
      'flex items-center px-5 py-2.5 gap-x-2 lg:gap-x-6 border border-grey-soft rounded-full font-[500] font-sora leading-[20px] w-[250px] overflow-hidden',
    optionsContainer: 'fixed z-50',
    options: `overflow-y-auto z-20 space-y-1 scrollbar-w-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-grey-soft [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-white',
`,
    option:
      'cursor-pointer select-none py-2 rounded-[22px] mx-1 px-4 flex items-center gap-x-2 font-[500] text-grey-slate break-all',
  },
  variants: {
    theme: {
      light: {
        button: 'text-[#0F0F0F] bg-white',
        options: 'border border-grey-soft bg-white',
        option: 'hover:bg-[#F3F3F3]/50',
      },
      transparent: {
        button: 'text-[#B4B4B4] bg-transparent',
        options: 'border border-grey-soft bg-transparent',
        option: 'text-[#B4B4B4]',
      },
    },
    openUpwards: {
      false: {
        button:
          'data-[open]:border-b-0 data-[open]:rounded-b-[0px] data-[open]:rounded-t-[23px]',
        options: 'pt-1 pb-6 border-t-0 rounded-b-[23px]',
      },
      true: {
        button:
          'data-[open]:border-t-0 data-[open]:rounded-t-[0px] data-[open]:rounded-b-[23px]',
        options: 'pt-6 pb-1 border-b-0 rounded-t-[23px]',
      },
    },
  },
  defaultVariants: {
    theme: 'light',
    openUpwards: false,
  },
})

const Select: React.FC<SelectProps> = ({
  label,
  options = [],
  multiple = false,
  value = multiple ? [] : null,
  onChange,
  placeholder = trans(SELECT),
  buttonClassName,
  theme,
  className,
  openUpwards = false,
  maxHeight = 300,
}) => {
  const styles = selectStyles({ theme, openUpwards })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [buttonWidth, setButtonWidth] = useState<number>(0)
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null)
  const [mounted, setMounted] = useState(false)
  const [availableSpace, setAvailableSpace] = useState({ above: 0, below: 0 })
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  useEffect(() => {
    const updateDimensions = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect()
        setButtonWidth(rect.width)
        setButtonRect(rect)

        // Calculate available space above and below
        const spaceAbove = rect.top
        const spaceBelow = window.innerHeight - rect.bottom
        setAvailableSpace({ above: spaceAbove, below: spaceBelow })
      }
    }

    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    window.addEventListener('scroll', updateDimensions, true)

    return () => {
      window.removeEventListener('resize', updateDimensions)
      window.removeEventListener('scroll', updateDimensions, true)
    }
  }, [])

  const computedValue = React.useMemo(() => {
    if (value) return value
    if (multiple) {
      return options.filter((opt) => !!opt?.selected)
    }
    return options.find((opt) => !!opt?.selected) || null
  }, [value, options, multiple])

  const handleChange = (newValue: SelectValueProps | SelectValueProps[]) => {
    if (!onChange) return

    if (multiple) {
      onChange(newValue)
    } else {
      const currentValue = value as SelectValueProps
      const newSingleValue = newValue as SelectValueProps

      if (currentValue?.value === newSingleValue?.value) {
        onChange(null)
      } else {
        onChange(newValue)
      }
    }
  }

  const getDisplayValue = () => {
    if (multiple && Array.isArray(value) && value.length > 0) {
      if (value.length > 1) {
        return `${value.length} ${trans(SELECTED_PLURAL)}`
      }
      return value.map((item) => item?.label).join(', ')
    }
    return (value as SelectValueProps)?.label || placeholder
  }

  const calculateMaxHeight = () => {
    // Determine the available space
    const space = openUpwards ? availableSpace.above : availableSpace.below
    // Use either the available space or the maxHeight prop, whichever is smaller
    return Math.min(space * 0.9, maxHeight)
  }

  const getDropdownPosition = () => {
    if (!buttonRect || !mounted) return {}

    return {
      position: 'fixed' as const,
      width: `${buttonWidth}px`,
      left: `${buttonRect.left}px`,
      ...(openUpwards
        ? { bottom: `${window.innerHeight - buttonRect.top}px` }
        : { top: `${buttonRect.bottom}px` }),
    }
  }

  return (
    <Field className={twMerge(styles.base(), className)}>
      {label && <Label>{label}</Label>}

      <div className="relative w-full">
        <Listbox
          value={computedValue}
          onChange={handleChange}
          multiple={multiple}
          by="value"
        >
          {({ open }: { open: boolean }) => {
            // Mettre à jour l'état ouvert/fermé
            if (open !== isOpen) {
              setIsOpen(open)
            }

            return (
              <>
                <ListboxButton
                  ref={buttonRef}
                  className={twMerge(
                    styles.button(),
                    '[&[data-open]_svg]:rotate-180',
                    buttonClassName,
                    'relative z-10',
                  )}
                >
                  <span className="truncate">{getDisplayValue()}</span>
                  <Icon
                    className="transition duration-200 ltr:ml-auto rtl:mr-auto"
                    name="chevron_down"
                    size={11}
                  />
                </ListboxButton>

                {mounted && (
                  <Transition
                    show={open}
                    enter="transition ease-in duration-100"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    className="z-50"
                    beforeEnter={() => {
                      // Recalculate dimensions before opening
                      if (buttonRef.current) {
                        const rect = buttonRef.current.getBoundingClientRect()
                        setButtonRect(rect)
                        const spaceAbove = rect.top
                        const spaceBelow = window.innerHeight - rect.bottom
                        setAvailableSpace({
                          above: spaceAbove,
                          below: spaceBelow,
                        })
                      }
                    }}
                  >
                    <div
                      style={getDropdownPosition()}
                      className="overflow-hidden"
                    >
                      <ListboxOptions
                        static
                        className={twMerge(
                          styles.options(),
                          'w-full',
                          'max-h-[var(--max-height)]',
                        )}
                        style={
                          {
                            '--max-height': `${calculateMaxHeight()}px`,
                          } as React.CSSProperties
                        }
                      >
                        {options.map((option) => (
                          <ListboxOption
                            key={option?.value}
                            value={option}
                            className="focus:outline-none"
                          >
                            {({
                              selected,
                              focus,
                            }: {
                              selected: boolean
                              focus: boolean
                            }) => (
                              <div
                                className={twMerge(
                                  styles.option(),
                                  'group',
                                  focus ? 'bg-[#F3F3F3]/70 font-bold' : '',
                                  selected ? 'font-bold' : '',
                                )}
                                data-focus={focus}
                                data-selected={selected}
                              >
                                <span className="flex-1 truncate">
                                  {option?.label}
                                </span>
                                {selected ? (
                                  <Icon
                                    className="ml-auto min-w-[22px] min-h-[22px]"
                                    name="checked"
                                    size={22}
                                  />
                                ) : (
                                  <span className="ml-auto rounded-full w-[22px] h-[22px] border-[1px] border-grey-soft "></span>
                                )}
                              </div>
                            )}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </div>
                  </Transition>
                )}
              </>
            )
          }}
        </Listbox>
      </div>
    </Field>
  )
}

export default Select
